import React from 'react';
import PropTypes from 'prop-types';
import '../../polyfills/polyfill-allSettled';
import ContainerWrapper from '../styled components/ContainerWrapper';
import CacheResultsContext from '../components/CacheResultsContext';
import DateNavigationUrlWrapper from '../components/DateNavigationUrlWrapper';
import WeekNavigation from '../components/WeekNavigation/WeekNavigation';
import RequestHandler from '../libs/requestHandling';
import NavigationHistory from '../libs/NavigationHistory';
import {
  getWeekDaysBetweenNotInFuture,
  serializeYesplanAPIDate,
  todayDateObject
} from '../libs/dateTimeUtilities';
import TimesheetsWeekView from '../timesheets/TimesheetsWeekView/TimesheetsWeekView';
import { formatTimesheets } from '../libs/ypAPIDataFormatter';
import { cacheResultForTimesheetsKey } from '../libs/cacheKeys';
import TimesheetsError from '../components/Errors/TimesheetsError';
import NoPermissionOnResourceError from '../components/Errors/NoPermissionOnResourceError';
import NotInTeamUsingTimesheetsError from '../components/Errors/NotInTeamUsingTimesheetsError';

class TimesheetsContainer extends React.Component {
  static widgetName = 'timesheets';

  static getDerivedStateFromProps(props) {
    const { dateObject, week } = props.getDateAndWeekFromUrlOrFromCurrentDate(
      TimesheetsContainer.widgetName
    );
    return {
      currentDateObject: dateObject,
      currentWeek: week
    };
  }

  constructor(props) {
    super(props);

    const { replaceUrlWithWeekAndDateUrl, getDateAndWeekFromUrlOrFromCurrentDate } =
      props;
    const { week } = getDateAndWeekFromUrlOrFromCurrentDate(
      TimesheetsContainer.widgetName
    );

    replaceUrlWithWeekAndDateUrl(TimesheetsContainer.widgetName);

    this.state = {
      currentWeek: week,
      allContentFetched: false,
      timesheets: {},
      errorCode: false
    };

    ['renderContent'].forEach((eventHandler) => {
      this[eventHandler] = this[eventHandler].bind(this);
    });
  }

  componentDidMount() {
    this.fetchTimesheetsAndCounters();
  }

  componentWillUnmount() {
    const { navigationHistory, resetCacheResultValue, setCacheResultValue } = this.props;
    if (
      navigationHistory.isNavigatingBetweenWidgets() &&
      !navigationHistory.isNavigatingInSameWidget()
    ) {
      setCacheResultValue(cacheResultForTimesheetsKey, this.state.timesheets);
    } else {
      resetCacheResultValue(cacheResultForTimesheetsKey);
    }
  }

  getDaysToRender(week) {
    return getWeekDaysBetweenNotInFuture(week.startDateObject, week.endDateObject);
  }

  fetchTimesheetsAndCounters() {
    const { user, getDateAndWeekFromUrlOrFromCurrentDate } = this.props;
    const { week } = getDateAndWeekFromUrlOrFromCurrentDate(
      TimesheetsContainer.widgetName
    );

    if (this.getDaysToRender(week).length === 0) {
      this.setState({
        allContentFetched: true
      });
    } else {
      const { startDateObject, endDateObject } = week;
      let lastDayToRender = endDateObject;

      if (endDateObject.isAfter(todayDateObject())) lastDayToRender = todayDateObject();
      const fromDateString = serializeYesplanAPIDate(startDateObject);
      const toDateString = serializeYesplanAPIDate(lastDayToRender);
      new RequestHandler().get(
        '/timesheets',
        {
          yesplanurl: user.host,
          userid: user.id,
          resourceid: user.resource.id,
          from: fromDateString,
          to: toDateString
        },
        (timesheetsResult, errorTimesheets) => {
          if (errorTimesheets) {
            this.setState({
              errorCode: errorTimesheets.code
            });
          } else {
            this.setState({
              timesheets: formatTimesheets(timesheetsResult.body),
              allContentFetched: true
            });
          }
        }
      );
    }
  }

  renderErrorMessage() {
    const { widgetPageHeaderRef, navigationHistory } = this.props;
    const { errorCode } = this.state;

    return (
      <ContainerWrapper
        $paddingTop={
          widgetPageHeaderRef.current ? widgetPageHeaderRef.current.getHeaderHeight() : 0
        }
        data-test-type="errorMessage"
      >
        <TimesheetsError errorCode={errorCode} navigationHistory={navigationHistory} />
      </ContainerWrapper>
    );
  }

  renderNoPermission(Component) {
    return (
      <>
        <div style={{ paddingTop: '48px' }} />
        <Component title={i18n('Sorry! No timesheets for you… yet')} />
      </>
    );
  }

  renderContent() {
    const { goToUrlForNextWeek, goToUrlForPreviousWeek, humanResource } = this.props;
    const { currentWeek, allContentFetched, timesheets, errorCode } = this.state;
    if (errorCode) return this.renderErrorMessage();
    if (humanResource === null) return this.renderNoPermission(NoPermissionOnResourceError);
    if (!humanResource.usetimesheets) return this.renderNoPermission(NotInTeamUsingTimesheetsError);
    return (
      <ContainerWrapper>
        <div tw="relative mt-48">
          <WeekNavigation
            currentWeek={currentWeek}
            goToPreviousWeek={() =>
              goToUrlForPreviousWeek(TimesheetsContainer.widgetName, currentWeek)
            }
            goToNextWeek={() =>
              goToUrlForNextWeek(TimesheetsContainer.widgetName, currentWeek)
            }
          />

          <TimesheetsWeekView
            {...this.props}
            week={currentWeek}
            allContentFetched={allContentFetched}
            timesheets={timesheets}
          />
        </div>
      </ContainerWrapper>
    );
  }

  render() {
    return this.renderContent();
  }
}

TimesheetsContainer.propTypes = {
  user: PropTypes.shape({
    host: PropTypes.string,
    id: PropTypes.string,
    preferences: PropTypes.shape({
      eventsFiltersApp: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    resource: PropTypes.shape({
      id: PropTypes.string.isRequired
    })
  }).isRequired,
  humanResource: PropTypes.shape(),
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  widgetPageHeaderRef: PropTypes.shape(),
  linkUrlFor: PropTypes.func.isRequired,
  getDateAndWeekFromUrlOrFromCurrentDate: PropTypes.func.isRequired,
  goToUrlForPreviousWeek: PropTypes.func.isRequired,
  goToUrlForNextWeek: PropTypes.func.isRequired,
  replaceUrlWithWeekAndDateUrl: PropTypes.func.isRequired,
  getCacheResultValue: PropTypes.func.isRequired,
  resetCacheResultValue: PropTypes.func.isRequired,
  setCacheResultValue: PropTypes.func.isRequired
};

TimesheetsContainer.defaultProps = {
  widgetPageHeaderRef: null,
  humanResource: null
};

function ForwardRef(props, ref) {
  return (
    <CacheResultsContext.Consumer>
      {(cacheResultsContextProps) => (
        <DateNavigationUrlWrapper
          {...props}
          {...cacheResultsContextProps}
          headerRef={ref}
        >
          {(propsFromWrapper) => <TimesheetsContainer {...propsFromWrapper} />}
        </DateNavigationUrlWrapper>
      )}
    </CacheResultsContext.Consumer>
  );
}

export default React.forwardRef(ForwardRef);
